/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import Fab from "@material-ui/core/Fab";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { FormControl, InputLabel, Select, MenuItem, TablePagination, Box } from "@material-ui/core";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import DescriptionIcon from "@material-ui/icons/Description";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import RepeatIcon from "@material-ui/icons/Repeat";
import StopIcon from "@material-ui/icons/Stop";
import ScheduleIcon from "@material-ui/icons/Schedule";
import AddIcon from "@material-ui/icons/Add";
import CampaignIcon from "@material-ui/icons/SendOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import CampaignModal from "../../components/CampaignModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_CAMPAIGNS") {
    const campaigns = action.payload;
    const newCampaigns = [];
    if (isArray(campaigns)) {
      campaigns.forEach((campaign) => {
        const idx = state.findIndex((u) => u.id === campaign.id);
        if (idx !== -1) state[idx] = campaign; else newCampaigns.push(campaign);
      });
    }
    return [...state, ...newCampaigns];
  }
  if (action.type === "UPDATE_CAMPAIGNS") {
    const campaign = action.payload;
    const idx = state.findIndex((u) => u.id === campaign.id);
    if (idx !== -1) { state[idx] = campaign; return [...state]; }
    return [campaign, ...state];
  }
  if (action.type === "DELETE_CAMPAIGN") {
    const idx = state.findIndex((u) => u.id === action.payload);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const statusConfig = {
  INATIVA:      { label: "Inactiva",     bg: "rgba(0,0,0,0.08)",      color: "#555" },
  PROGRAMADA:   { label: "Programada",   bg: "rgba(91,45,142,0.12)",  color: "#5B2D8E" },
  EM_ANDAMENTO: { label: "En curso",     bg: "rgba(34,197,94,0.12)",  color: "#166534" },
  CANCELADA:    { label: "Cancelada",    bg: "#fee2e2",               color: "#991b1b" },
  FINALIZADA:   { label: "Finalizada",   bg: "rgba(0,0,0,0.06)",      color: "#333" },
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1, padding: theme.spacing(1),
    overflowY: "scroll", ...theme.scrollbarStyles,
  },
  mobileList: {
    display: "flex", flexDirection: "column",
    gap: theme.spacing(1.5), padding: theme.spacing(1),
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10, padding: theme.spacing(1.5),
    display: "flex", flexDirection: "column", gap: theme.spacing(1),
  },
  cardTop: { display: "flex", alignItems: "center", gap: theme.spacing(1) },
  cardName: {
    fontWeight: 700, fontSize: "0.95rem",
    color: theme.palette.text.primary, flex: 1, minWidth: 0,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
  cardMeta: {
    fontSize: "0.78rem", color: theme.palette.text.secondary,
    display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
  },
  cardActions: { display: "flex", alignItems: "center", gap: 2 },
  cardBottom: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  statusChip: { fontSize: "0.72rem", height: 20, borderRadius: 4, fontWeight: 600 },
  mobileHeader: {
    display: "flex", flexDirection: "column",
    gap: theme.spacing(1), width: "100%", padding: theme.spacing(0, 0, 1),
  },
  mobileHeaderRow: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  mobileFilters: {
    display: "flex", gap: theme.spacing(1),
    "& > *": { flex: 1 },
  },
  fab: { position: "fixed", bottom: theme.spacing(3), right: theme.spacing(3), zIndex: 10 },
  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary, gap: theme.spacing(1),
  },
  recurringChip: { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText, fontSize: "0.75rem" },
  tableHeader: { fontWeight: "bold" },
}));

const Campaigns = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [stopRecurrenceModalOpen, setStopRecurrenceModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [campaigns, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      try {
        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);
        if (!planConfigs?.plan?.useCampaigns) {
          toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
          setTimeout(() => history.push(`/`), 1000);
        }
      } catch (err) {}
    }
    fetchData();
  }, []);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam, statusFilter, pageSize]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => { fetchCampaigns(); }, 500);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber, statusFilter, pageSize]);

  useEffect(() => {
    const companyId = user.companyId;
    const onEvent = (data) => {
      if (data.action === "update" || data.action === "create") dispatch({ type: "UPDATE_CAMPAIGNS", payload: data.record });
      if (data.action === "delete") dispatch({ type: "DELETE_CAMPAIGN", payload: +data.id });
    };
    socket.on(`company-${companyId}-campaign`, onEvent);
    return () => socket.off(`company-${companyId}-campaign`, onEvent);
  }, [user]);

  const fetchCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns/", { params: { searchParam, pageNumber, pageSize, status: statusFilter } });
      if (pageNumber === 1) dispatch({ type: "RESET" });
      dispatch({ type: "LOAD_CAMPAIGNS", payload: data.records });
      setTotalCount(data.count || 0);
      setLoading(false);
    } catch (err) { toastError(err); }
  };

  const handleOpenModal = () => { setSelectedCampaign(null); setCampaignModalOpen(true); };
  const handleCloseModal = () => { setSelectedCampaign(null); setCampaignModalOpen(false); };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/campaigns/${id}`);
      toast.success(i18n.t("campaigns.toasts.deleted"));
    } catch (err) { toastError(err); }
    setDeletingCampaign(null); setSearchParam(""); setPageNumber(1);
  };

  const handleStopRecurrence = async (id) => {
    try {
      await api.post(`/campaigns/${id}/stop-recurrence`);
      toast.success("Recorrência interrompida com sucesso!");
      setPageNumber(1); fetchCampaigns();
    } catch (err) { toastError(err); }
    setStopRecurrenceModalOpen(false); setSelectedCampaign(null);
  };

  const cancelCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setPageNumber(1); fetchCampaigns();
    } catch (err) { toast.error(err.message); }
  };

  const restartCampaign = async (campaign) => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setPageNumber(1); fetchCampaigns();
    } catch (err) { toast.error(err.message); }
  };

  const formatStatus = (val) => statusConfig[val]?.label || val;
  const getStatusStyle = (val) => statusConfig[val] || { bg: "rgba(0,0,0,0.06)", color: "#333" };

  const formatRecurrenceType = (type) => {
    const map = { minutely: "Por Minuto", hourly: "Por Hora", daily: "Diario", weekly: "Semanal", biweekly: "Quincenal", monthly: "Mensual", yearly: "Anual" };
    return map[type] || type;
  };

  const renderCampaignActions = (campaign, size = "small") => (
    <>
      {campaign.status === "EM_ANDAMENTO" && (
        <Tooltip title="Parar"><IconButton size={size} onClick={() => cancelCampaign(campaign)}><PauseCircleOutlineIcon fontSize={size} /></IconButton></Tooltip>
      )}
      {campaign.status === "CANCELADA" && (
        <Tooltip title="Reiniciar"><IconButton size={size} onClick={() => restartCampaign(campaign)}><PlayCircleOutlineIcon fontSize={size} /></IconButton></Tooltip>
      )}
      {campaign.isRecurring && campaign.status !== "FINALIZADA" && (
        <Tooltip title="Detener recurrencia">
          <IconButton size={size} color="secondary" onClick={() => { setSelectedCampaign(campaign); setStopRecurrenceModalOpen(true); }}>
            <StopIcon fontSize={size} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Reporte"><IconButton size={size} onClick={() => history.push(`/campaign/${campaign.id}/report`)}><DescriptionIcon fontSize={size} /></IconButton></Tooltip>
      <Tooltip title="Editar"><IconButton size={size} onClick={() => { setSelectedCampaign(campaign); setCampaignModalOpen(true); }}><EditIcon fontSize={size} /></IconButton></Tooltip>
      <Tooltip title="Eliminar">
        <IconButton size={size} onClick={() => { setConfirmModalOpen(true); setDeletingCampaign(campaign); }}>
          <DeleteOutlineIcon fontSize={size} />
        </IconButton>
      </Tooltip>
    </>
  );

  const renderMobileCard = (campaign) => {
    const st = getStatusStyle(campaign.status);
    return (
      <div key={campaign.id} className={classes.card}>
        <div className={classes.cardTop}>
          {campaign.isRecurring && <RepeatIcon fontSize="small" color="primary" />}
          <div className={classes.cardName}>{campaign.name}</div>
          <span style={{ fontSize: "0.72rem", fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: st.bg, color: st.color, flexShrink: 0 }}>
            {st.label}
          </span>
        </div>
        <div className={classes.cardMeta}>
          {campaign.whatsapp?.name && <span>📱 {campaign.whatsapp.name}</span>}
          {campaign.contactList?.name && <span>👥 {campaign.contactList.name}</span>}
          {campaign.scheduledAt && (
            <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
              <ScheduleIcon style={{ fontSize: 13 }} />{datetimeToClient(campaign.scheduledAt)}
            </span>
          )}
          {campaign.isRecurring && campaign.recurrenceType && (
            <span style={{ fontWeight: 600, color: theme.palette.primary.main }}>{formatRecurrenceType(campaign.recurrenceType)}</span>
          )}
        </div>
        <div className={classes.cardBottom}>
          <div className={classes.cardActions}>{renderCampaignActions(campaign)}</div>
        </div>
      </div>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingCampaign && `${i18n.t("campaigns.confirmationModal.deleteTitle")} ${deletingCampaign.name}?`}
        open={confirmModalOpen} onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deletingCampaign.id)}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ConfirmationModal
        title="Interromper Recorrência" open={stopRecurrenceModalOpen}
        onClose={() => setStopRecurrenceModalOpen(false)}
        onConfirm={() => handleStopRecurrence(selectedCampaign?.id)}
      >
        Tem certeza que deseja interromper a recorrência desta campanha?
      </ConfirmationModal>
      {campaignModalOpen && (
        <CampaignModal
          resetPagination={() => { setPageNumber(1); fetchCampaigns(); }}
          open={campaignModalOpen} onClose={handleCloseModal}
          aria-labelledby="form-dialog-title"
          campaignId={selectedCampaign && selectedCampaign.id}
        />
      )}

      {user.profile === "user" && user?.showCampaign === "disabled" ? <ForbiddenPage /> : (
        <>
          {isMobile ? (
            <>
              <MainHeader>
                <div className={classes.mobileHeader}>
                  <div className={classes.mobileHeaderRow}>
                    <Title>{i18n.t("campaigns.title")}</Title>
                    <Typography variant="caption" color="textSecondary">{totalCount} campañas</Typography>
                  </div>
                  <TextField
                    fullWidth size="small" variant="outlined"
                    placeholder={i18n.t("campaigns.searchPlaceholder")}
                    type="search" value={searchParam} onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: "gray" }} fontSize="small" /></InputAdornment> }}
                  />
                  <div className={classes.mobileFilters}>
                    <FormControl variant="outlined" size="small">
                      <InputLabel>Status</InputLabel>
                      <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="INATIVA">Inactiva</MenuItem>
                        <MenuItem value="PROGRAMADA">Programada</MenuItem>
                        <MenuItem value="EM_ANDAMENTO">En curso</MenuItem>
                        <MenuItem value="CANCELADA">Cancelada</MenuItem>
                        <MenuItem value="FINALIZADA">Finalizada</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                </div>
              </MainHeader>
              <Paper className={classes.mainPaper} variant="outlined">
                {campaigns.length === 0 && !loading ? (
                  <div className={classes.emptyState}>
                    <CampaignIcon style={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography variant="body2">Sin campañas</Typography>
                  </div>
                ) : (
                  <div className={classes.mobileList}>
                    {campaigns.map(renderMobileCard)}
                    {loading && <Typography variant="caption" align="center" color="textSecondary">Cargando...</Typography>}
                  </div>
                )}
              </Paper>
              <Fab color="primary" className={classes.fab} onClick={handleOpenModal}><AddIcon /></Fab>
            </>
          ) : (
            <>
              <MainHeader>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 16 }}>
                  <Title>{i18n.t("campaigns.title")}</Title>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <TextField
                      placeholder={i18n.t("campaigns.searchPlaceholder")}
                      type="search" value={searchParam} onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
                      size="small" variant="outlined"
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: "gray" }} /></InputAdornment> }}
                    />
                    <Button variant="contained" color="primary" onClick={handleOpenModal}>
                      {i18n.t("campaigns.buttons.add")}
                    </Button>
                  </div>
                </div>
              </MainHeader>

              <Paper style={{ padding: 12, marginBottom: 8 }} variant="outlined">
                <Box display="flex" style={{ gap: 12 }}>
                  <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
                    <InputLabel>Filtrar por Status</InputLabel>
                    <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Filtrar por Status">
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="INATIVA">Inactiva</MenuItem>
                      <MenuItem value="PROGRAMADA">Programada</MenuItem>
                      <MenuItem value="EM_ANDAMENTO">En curso</MenuItem>
                      <MenuItem value="CANCELADA">Cancelada</MenuItem>
                      <MenuItem value="FINALIZADA">Finalizada</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Paper>

              <Paper className={classes.mainPaper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" className={classes.tableHeader}>{i18n.t("campaigns.table.name")}</TableCell>
                      <TableCell align="center" className={classes.tableHeader}>{i18n.t("campaigns.table.status")}</TableCell>
                      <TableCell align="center" className={classes.tableHeader}>Recurrencia</TableCell>
                      <TableCell align="center" className={classes.tableHeader}>{i18n.t("campaigns.table.contactList")}</TableCell>
                      <TableCell align="center" className={classes.tableHeader}>{i18n.t("campaigns.table.whatsapp")}</TableCell>
                      <TableCell align="center" className={classes.tableHeader}>{i18n.t("campaigns.table.scheduledAt")}</TableCell>
                      <TableCell align="center" className={classes.tableHeader}>{i18n.t("campaigns.table.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {campaigns.map((campaign) => {
                      const st = getStatusStyle(campaign.status);
                      return (
                        <TableRow key={campaign.id}>
                          <TableCell align="center">
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                              {campaign.isRecurring && <Tooltip title="Campaña Recurrente"><RepeatIcon color="primary" fontSize="small" /></Tooltip>}
                              {campaign.name}
                            </div>
                          </TableCell>
                          <TableCell align="center">
                            <span style={{ fontSize: "0.75rem", fontWeight: 600, padding: "2px 10px", borderRadius: 4, background: st.bg, color: st.color }}>
                              {st.label}
                            </span>
                          </TableCell>
                          <TableCell align="center">
                            {campaign.isRecurring
                              ? <Chip label={formatRecurrenceType(campaign.recurrenceType)} size="small" className={classes.recurringChip} />
                              : <Chip label="Única" variant="outlined" size="small" />}
                          </TableCell>
                          <TableCell align="center">
                            {campaign.contactListId ? campaign.contactList?.name || "Lista removida"
                              : campaign.tagListId && campaign.tagListId !== "Nenhuma" ? `Tag: ${campaign.tagListId}` : "—"}
                          </TableCell>
                          <TableCell align="center">{campaign.whatsapp?.name || "—"}</TableCell>
                          <TableCell align="center">{campaign.scheduledAt ? datetimeToClient(campaign.scheduledAt) : "—"}</TableCell>
                          <TableCell align="center">
                            <div style={{ display: "flex", justifyContent: "center", gap: 2 }}>
                              {renderCampaignActions(campaign)}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {loading && <TableRowSkeleton columns={7} />}
                  </TableBody>
                </Table>
                <Box display="flex" justifyContent="flex-end" p={1}>
                  <TablePagination
                    component="div" count={totalCount}
                    page={pageNumber - 1}
                    onPageChange={(e, newPage) => setPageNumber(newPage + 1)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPageNumber(1); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Filas:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                  />
                </Box>
              </Paper>
            </>
          )}
        </>
      )}
    </MainContainer>
  );
};

export default Campaigns;
